import { useMemo } from 'react';

import { DocTypeOption, useDocumentTypes } from '@/features/DocumentSearch/api';
import { DocType, docTypeLabel } from '@/models/docs';

import { generateFileTypeAbbreviation } from '@/lib/utils';

/**
 * Every known `DocType` enum member as a combobox option, labelled via
 * `labelFn` (defaults to the plain `docTypeLabel`). Shared by
 * `mergeDocTypeOptions` below and `GroundTruthOverview`'s doc-type filter,
 * which passes a formatter that appends an abbreviation — callers control
 * both the label and (by sorting or not) the resulting order.
 */
function knownDocTypeOptions(
    labelFn: (docType: DocType) => string = docTypeLabel,
): DocTypeOption[] {
    return Object.values(DocType).map((value) => ({
        value,
        label: labelFn(value),
    }));
}

/**
 * Union of every known `DocType` enum member with any custom/legacy type
 * strings already present in the team's data (deduped by `value`, known
 * types win the label on overlap). Known and properly named types are sorted
 * alphabetically by label, followed by unnamed customs in API order. `labelFn`
 * overrides the label of known types (see `knownDocTypeOptions`).
 */
export function mergeDocTypeOptions(
    customTypes: DocTypeOption[],
    labelFn?: (docType: DocType) => string,
): DocTypeOption[] {
    const knownTypes = knownDocTypeOptions(labelFn).sort((a, b) =>
        a.label.localeCompare(b.label),
    );

    const knownValues = new Set<string>(knownTypes.map((t) => t.value));
    const customOnly = customTypes.filter((t) => !knownValues.has(t.value));
    const named = customOnly.filter((t) => !isUnnamedDocType(t));
    const unnamed = customOnly.filter(isUnnamedDocType);

    return [
        ...[...knownTypes, ...named].sort((a, b) =>
            a.label.localeCompare(b.label),
        ),
        ...unnamed,
    ];
}

const KNOWN_DOC_TYPES = new Set<string>(Object.values(DocType));

/** A type outside the enum that the API gives no real name: its label is missing or just the raw value. */
export function isUnnamedDocType(item: {
    value: string;
    label: string;
}): boolean {
    return (
        !KNOWN_DOC_TYPES.has(item.value) &&
        (item.label.trim() === '' ||
            item.label.toLowerCase() === item.value.toLowerCase())
    );
}

/** Marks an unnamed custom type's label so it can't pass for a standard one; every other type comes back unchanged. */
export function flagCustomDocType<T extends { value: string; label: string }>(
    item: T,
): T {
    if (!isUnnamedDocType(item)) return item;
    return { ...item, label: `${item.label} · not a standard type` };
}

/**
 * GT Hub combobox data source: all known doc types unioned with any
 * custom/legacy types already in the team's data. Unlike `useDocumentTypes`
 * (used as-is by the Document Search filter, which should keep showing only
 * types-in-use), this is specific to the GT split editors.
 */
export function useDocTypeOptions() {
    const { data: customTypes, ...rest } = useDocumentTypes();

    const docTypeOptions = useMemo(
        () => mergeDocTypeOptions(customTypes ?? []),
        [customTypes],
    );

    return { data: docTypeOptions, ...rest };
}

/** "Name (ABBR)", leaving a label that already ends in "(...)" as it is. */
export function labelWithAbbreviation(label: string): string {
    return label.trimEnd().endsWith(')')
        ? label
        : `${label} (${generateFileTypeAbbreviation(label)})`;
}

/** Stands for "no type" in the type dropdown; never stored, mapped to the unset value on select. */
export const NO_TYPE_VALUE = '__no_type__';

/** Puts a "No type (skip)" choice first, only while a type is set, so a type can be cleared. */
export function withNoTypeOption<T extends { value: string; label: string }>(
    items: T[],
    hasType: boolean,
): { value: string; label: string }[] {
    return hasType
        ? [{ value: NO_TYPE_VALUE, label: 'No type (skip)' }, ...items]
        : items;
}
